import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EmptyState } from "../../components/EmptyState";
import { KeepoLoader } from "../../components/KeepoLoader";
import { Box } from "../../components/Layout";
import { Skeleton } from "../../components/Skeleton";
import { SubHeader } from "../../components/SubHeader";
import { useAppState } from "../../contexts/App";
import { useSitePath } from "../../hooks/SitePath";
import { Contact, listContacts } from "../../services/contacts";
import { ContactCard } from "./ContactCard";
import { Intro, List, LoadingMore, LoadingScreen, Note } from "./styles";

const prefix = "pages.contacts";

/**
 * Mensagens (/contacts): what arrived through the Keepo's form, newest first, 20 at a time (the next ones as the list's end
 * shows). Each one opens with all its fields.
 */
const ContactsPage = () => {
  const { t } = useTranslation();
  const sitePath = useSitePath();
  const navigate = useNavigate();
  const { appState } = useAppState();
  const siteId = appState.editingSiteId ?? "";
  const [contacts, setContacts] = useState<Contact[]>();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasError, setError] = useState(false);

  const load = useCallback(() => {
    if (!siteId) return;
    let current = true;
    // another Keepo: never the contacts of the one before while its own load
    setContacts(undefined);
    setLoading(true);
    setLoadingMore(false);
    setError(false);
    listContacts(siteId)
      .then(list => {
        if (!current) return;
        setContacts(list.items);
        setTotal(list.total);
        setPage(1);
      })
      .catch(() => current && setError(true))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [siteId]);

  useEffect(load, [load]);

  // The Keepo on screen now: a next page that arrives after switching Keepos is another Keepo's, so it's dropped
  const shownSite = useRef(siteId);
  shownSite.current = siteId;

  const loadMore = useCallback(() => {
    const requested = siteId;
    const stale = () => shownSite.current !== requested;
    setLoadingMore(true);
    setError(false);
    listContacts(siteId, page + 1)
      .then(list => {
        if (stale()) return;
        setContacts(shown => [
          ...(shown ?? []),
          ...list.items.filter(item => !shown?.some(contact => contact.id === item.id)),
        ]);
        setTotal(list.total);
        setPage(page + 1);
      })
      .catch(() => !stale() && setError(true))
      .finally(() => !stale() && setLoadingMore(false));
  }, [siteId, page]);

  // The next page as the list's end shows (the button only where the browser can't tell); nothing said at the last
  const hasMore = !!contacts && contacts.length < total && !loading && !hasError;
  const canWatchEnd = typeof window.IntersectionObserver === "function";
  const listEnd = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const end = listEnd.current;
    if (!hasMore || loadingMore || !canWatchEnd || !end) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect();
          loadMore();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, canWatchEnd, loadMore]);

  return (
    <>
      <SubHeader title={t(`${prefix}.title`)} crumbs={[]} />
      <Box>
        <Intro>{t(`${prefix}.intro`)}</Intro>

        {!contacts && loading && !hasError && (
          <LoadingScreen role="status" aria-label={t(`${prefix}.loading`)}>
            <Skeleton className="card" />
            <Skeleton className="card" />
            <Skeleton className="card" />
          </LoadingScreen>
        )}

        {hasError && (
          <Note role="alert" className="mt-4">
            {t(`${prefix}.loadError`)}{" "}
            <button type="button" onClick={contacts ? loadMore : load}>
              {t(`${prefix}.retry`)}
            </button>
          </Note>
        )}

        {contacts && (
          <List>
            {contacts.length === 0 && !hasError && (
              <EmptyState
                emoji="📬"
                title={t(`${prefix}.emptyTitle`)}
                description={t(`${prefix}.emptyText`)}
                // the form is a section of the Keepo: added and set up in the editor
                action={{ label: t(`${prefix}.emptyAction`), onClick: () => navigate(sitePath("/editor/sections")) }}
              />
            )}
            {contacts.map(contact => (
              <ContactCard key={contact.id} contact={contact} to={sitePath(`/contacts/${contact.id}`)} />
            ))}
          </List>
        )}

        <div ref={listEnd} />
        {loadingMore && (
          <LoadingMore role="status" aria-label={t(`${prefix}.loadingMore`)}>
            <KeepoLoader size={20} color="var(--color-neutral-white)" />
          </LoadingMore>
        )}
        {hasMore && !canWatchEnd && !loadingMore && (
          <Note className="text-center">
            <button type="button" onClick={loadMore}>
              {t(`${prefix}.loadMore`)}
            </button>
          </Note>
        )}
      </Box>
    </>
  );
};

export default ContactsPage;
